/**
 * The HTTP client.
 *
 * One place that knows the envelope, so no screen ever writes `res.data.data`. Every
 * response is `{ data, error }`; this unwraps `data` on success and THROWS an
 * `ApiFailure` carrying the `ERR_*` code on failure, so a caller's `catch` — or
 * TanStack Query's `isError` — is the single failure path.
 *
 * Deliberately `fetch` rather than axios. The whole surface used here is four verbs, a
 * JSON body and one header; axios adds ~14 kB gzipped to a bundle served over Sri
 * Lankan mobile connections to save about twenty lines.
 */

import { extractErrorCode, friendlyError } from '@serendib/shared';

/**
 * In development this is empty and Vite proxies `/api` to port 4001, so the browser
 * makes same-origin requests and CORS never enters the picture. In production it is
 * the API's own domain, baked in at build time.
 */
const BASE = (import.meta.env.VITE_API_URL ?? '').replace(/\/$/, '');

const TOKEN_KEY = 'serendib.token';

/* -------------------------------------------------------------------------- */
/* Token storage                                                               */
/* -------------------------------------------------------------------------- */

/**
 * `localStorage`, with every access guarded.
 *
 * It throws outright in a private window with site data blocked, and in some embedded
 * webviews — not "returns null", throws. An unguarded read at module scope takes the
 * whole application down before it renders, and the report is "the site is blank on my
 * phone".
 *
 * A token in `localStorage` is readable by any script that runs on this origin, which
 * is the accepted trade for a back office with no server-rendered session. The
 * mitigations are elsewhere: a 12-hour expiry, the account re-read on every request so
 * a deactivation takes effect immediately, and no `dangerouslySetInnerHTML` anywhere in
 * the app.
 */
export function readToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

export function writeToken(token: string | null): void {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  } catch {
    /* Sessions will not survive a reload. Everything else still works. */
  }
}

/* -------------------------------------------------------------------------- */
/* Errors                                                                      */
/* -------------------------------------------------------------------------- */

export class ApiFailure extends Error {
  readonly code: string;
  readonly status: number;
  readonly details?: unknown;

  constructor(code: string, status: number, details?: unknown) {
    super(code);
    this.name = 'ApiFailure';
    this.code = code;
    this.status = status;
    this.details = details;
  }

  /** The sentence to show a person. */
  get friendly(): string {
    return friendlyError(this.code);
  }

  /** Per-field validation messages, when the failure was a 400 from Zod. */
  get fieldErrors(): Record<string, string> {
    if (this.code !== 'ERR_VALIDATION' || !Array.isArray(this.details)) return {};
    const out: Record<string, string> = {};
    for (const issue of this.details as { path?: string; message?: string }[]) {
      if (issue.path && issue.message) out[issue.path] = issue.message;
    }
    return out;
  }
}

/**
 * Called when a request comes back 401 with an expired or invalid token.
 *
 * A callback rather than a direct import of the auth store, because the store imports
 * this module — a direct import would be a cycle, and the symptom of that cycle is an
 * `undefined` at module-init time in the production bundle only, where the chunking
 * differs from dev.
 */
let onUnauthorized: (() => void) | null = null;
export function setUnauthorizedHandler(handler: () => void): void {
  onUnauthorized = handler;
}

/* -------------------------------------------------------------------------- */
/* Request                                                                     */
/* -------------------------------------------------------------------------- */

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  /** Attach the bearer token. On by default; the public site's reads do not need it. */
  auth?: boolean;
  signal?: AbortSignal;
  /** For uploads: send as-is and let the browser set the multipart boundary. */
  formData?: FormData;
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, auth = true, signal, formData } = options;

  const headers: Record<string, string> = {};
  // NEVER set Content-Type for FormData. The browser has to add the multipart boundary
  // itself, and setting the header by hand omits it — the server then reads an empty
  // body and reports "no file was uploaded" for a request that plainly had one.
  if (!formData && body !== undefined) headers['Content-Type'] = 'application/json';

  if (auth) {
    const token = readToken();
    if (token) headers.Authorization = `Bearer ${token}`;
  }

  let response: Response;
  try {
    response = await fetch(`${BASE}/api${path}`, {
      method,
      headers,
      body: formData ?? (body === undefined ? undefined : JSON.stringify(body)),
      signal,
    });
  } catch (err) {
    // An aborted request is a component unmounting, not a failure to report.
    if (err instanceof DOMException && err.name === 'AbortError') throw err;
    throw new ApiFailure('ERR_NETWORK', 0);
  }

  // 204 and an empty 200 both legitimately carry no body.
  const text = await response.text();
  let payload: { data?: unknown; error?: string | null; details?: unknown } = {};
  if (text) {
    try {
      payload = JSON.parse(text) as typeof payload;
    } catch {
      // HTML from a proxy, a gateway timeout page, or Passenger's generic 500 —
      // anything that is not this API answering.
      throw new ApiFailure(response.ok ? 'ERR_INTERNAL' : 'ERR_UNAVAILABLE', response.status);
    }
  }

  if (!response.ok || payload.error) {
    const code = payload.error ?? extractErrorCode(payload) ?? 'ERR_INTERNAL';

    // Sign the user out only for a token that is actually bad. A 401 from the login
    // endpoint itself is a wrong password, and treating it as an expired session
    // clears storage and reloads the page under someone who was never signed in.
    if (response.status === 401 && !path.startsWith('/auth/login')) onUnauthorized?.();

    throw new ApiFailure(code, response.status, payload.details);
  }

  return payload.data as T;
}

export const api = {
  get: <T>(path: string, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'GET' }),
  post: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'POST', body }),
  put: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'PUT', body }),
  patch: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'PATCH', body }),
  delete: <T>(path: string, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'DELETE' }),
  upload: <T>(path: string, formData: FormData) => request<T>(path, { method: 'POST', formData }),
};

/** Build a query string, dropping empty values so `?search=` never reaches the server. */
export function qs(params: Record<string, unknown>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue;
    search.set(key, String(value));
  }
  const out = search.toString();
  return out ? `?${out}` : '';
}

export interface Paginated<T> {
  items: T[];
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
}
